import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";

export const DEFAULT_AUTO_CLOSE_DURATION_MS = 5000;

type Props = {
  durationMs?: number;
  onComplete: () => void;
};

/**
 * Ticking seconds label for modals that close on their own. Runs the countdown
 * itself and calls onComplete when it runs out.
 */
export const AutoCloseCountdown = ({
  durationMs = DEFAULT_AUTO_CLOSE_DURATION_MS,
  onComplete,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.ceil(durationMs / 1000),
  );

  // Kept in a ref so a caller re-creating its callback on every render can't
  // restart the countdown and keep the modal open forever.
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    setSecondsLeft(Math.ceil(durationMs / 1000));

    const intervalId = setInterval(() => {
      setSecondsLeft((previous) => Math.max(previous - 1, 0));
    }, 1000);
    const timeoutId = setTimeout(() => {
      onCompleteRef.current();
    }, durationMs);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [durationMs]);

  return (
    <Label>{t("DesignComponents.ClosingIn", { seconds: secondsLeft })}</Label>
  );
};

const Label = styled.span`
  ${TypographyV2.s1};
  color: ${ColorV2.Blue1};
  margin-top: 24px;
  text-align: center;
`;
